import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';

import { FilterAlt } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import { useFiltersCard } from './hooks';
import { ALL_OPTION } from '@common/types/constants';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Select, MultiSelect } from 'ui';
import { t } from 'i18next';
import { theme } from '@store';
import {
  MultiselectField,
  MultiselectSearchableField,
} from 'components/Multiselect';

export const FiltersCard = () => {
  const {
    handleClearAll,
    handleApply,
    draftFilters,
    taskProgramOptions,
    statusOptions,
    priorityOptions,
    categoryOptions,
    locationOptions,
    handleProgramOnChange,
    handleDueDateOnChange,
    handleCategoryOnChange,
    handleLocationOnChange,
    handlePriorityOnChange,
    handleCloseFiltersCard,
    handlePracticeOnChange,
    handleAssigneeOnChange,
    handleTaskNameOnChange,
    handleOrderCategoryOnChange,
    handleOrderTypeOnChange,
    handleLastUpdatedDateOnChange,
    handleStatusOnChange,
    practiceOptions,
    userOptions,
    teamOptions,
    isApplyEnabled,
    taskNameOptions,
    orderTypeOptions,
    orderCategoryOptions,
    handleTeamOnChange,
    isCompletedTaskTabActive,
    hideStatusFilter,
    isCompletedTab,
    isResetEnabled,
  } = useFiltersCard();

  const dueDateDayjs = dayjs(draftFilters.due_date);
  const dueDate = dueDateDayjs.isValid() ? dueDateDayjs : null;

  const lastUpdatedDateDayjs = dayjs(draftFilters.last_updated_after);
  const lastUpdatedDate = lastUpdatedDateDayjs.isValid()
    ? lastUpdatedDateDayjs
    : null;

  return (
    <Box
      sx={{
        minWidth: '260px',
        maxWidth: '260px',
        boxShadow: 'initial',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 200px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          padding: '0',
          pt: '1rem',
          pb: '.5rem',
          mb: '.5rem',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FilterAlt />
          <Typography variant='subtitle1'>Filters</Typography>
        </Box>
        <Box
          onClick={handleCloseFiltersCard}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            ':hover': {
              cursor: 'pointer',
              bgcolor: theme => theme.palette.grey[50],
              borderRadius: '6px',
            },
            height: '30px',
            width: '30px',
          }}
        >
          <ClearIcon />
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          maxHeight: `calc(100vh - 515px)`,
          overflowY: 'auto',
          [theme.breakpoints.down('md')]: {
            maxHeight: 'calc(100vh - 310px)',
          },
          pt: 1,
        }}
      >
        <Select
          name='practice'
          size='medium'
          label='Practice'
          onChange={handlePracticeOnChange}
          value={draftFilters.practice_id}
          options={practiceOptions}
          clearLabel='All'
          clearable
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
        />

        <Select
          name='team'
          size='medium'
          label='Team'
          onChange={handleTeamOnChange}
          value={draftFilters.team_id}
          options={teamOptions}
          clearLabel='All'
          clearable
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
        />

        <Select
          name='assignee'
          size='medium'
          label='Assignee'
          onChange={handleAssigneeOnChange}
          value={draftFilters.assignee_id}
          options={userOptions}
          clearLabel='All'
          clearable
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
        />

        <MultiselectSearchableField
          options={taskProgramOptions}
          value={
            Array.isArray(draftFilters.programs) ? draftFilters?.programs : []
          }
          name='programs'
          label='Service lines & programs'
          handleChange={values => handleProgramOnChange(values)}
          optional
          clearable
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
        />

        {!isCompletedTab && (
          <Box>
            <Typography>Priority</Typography>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                pl: '0.5rem',
                flexDirection: 'column',
              }}
            >
              <FormControlLabel
                sx={{
                  width: 'fit-content',
                }}
                control={
                  <Checkbox
                    checked={
                      draftFilters.priorities.length === priorityOptions.length
                    }
                  />
                }
                label={ALL_OPTION.label}
                onChange={() => handlePriorityOnChange(ALL_OPTION.value)}
              />
              {priorityOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  sx={{
                    width: 'fit-content',
                  }}
                  control={
                    <Checkbox
                      value={option.value}
                      checked={
                        draftFilters.priorities.includes(option.value) &&
                        !option.disabled
                      }
                    />
                  }
                  disabled={option.disabled}
                  label={option.label}
                  onChange={() => handlePriorityOnChange(option.value)}
                />
              ))}
            </Box>
          </Box>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
          <DatePicker
            format='MM/DD/YYYY'
            label={'Due date'}
            value={dueDate}
            onChange={val => {
              const formattedValue = val
                ? dayjs(val).format('MMM D, YYYY')
                : '';
              handleDueDateOnChange(formattedValue);
            }}
          />
        </LocalizationProvider>

        {!isCompletedTab && !hideStatusFilter && (
          <Box>
            <Typography>Status</Typography>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                pl: '0.5rem',
                flexDirection: 'column',
              }}
            >
              <FormControlLabel
                sx={{
                  width: 'fit-content',
                }}
                control={
                  <Checkbox
                    checked={
                      draftFilters.statuses.length === statusOptions.length
                    }
                  />
                }
                label={ALL_OPTION.label}
                onChange={() => handleStatusOnChange(ALL_OPTION.value)}
              />
              {statusOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  sx={{
                    width: 'fit-content',
                  }}
                  control={
                    <Checkbox
                      value={option.value}
                      checked={draftFilters.statuses.includes(option.value)}
                    />
                  }
                  label={option.label}
                  onChange={() => handleStatusOnChange(option.value)}
                />
              ))}
            </Box>
          </Box>
        )}

        <Select
          name='category'
          size='medium'
          label={'Task category'}
          onChange={handleCategoryOnChange}
          value={draftFilters.category}
          options={categoryOptions}
          clearable
          clearLabel='All'
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
        />

        <MultiselectSearchableField
          options={taskNameOptions}
          value={
            Array.isArray(draftFilters.task_names)
              ? draftFilters?.task_names
              : []
          }
          name={'task_names'}
          label={'Task names'}
          handleChange={values => handleTaskNameOnChange(values)}
          clearable
          optional
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
        />

        <MultiselectField
          options={orderCategoryOptions}
          value={
            Array.isArray(draftFilters.order_categories)
              ? draftFilters?.order_categories
              : []
          }
          name={'order_categories'}
          label={'Order categories'}
          handleChange={values => handleOrderCategoryOnChange(values)}
          optional
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
        />

        <MultiselectField
          options={orderTypeOptions}
          value={
            Array.isArray(draftFilters.order_types)
              ? draftFilters?.order_types
              : []
          }
          name={'order_types'}
          label={'Order types'}
          handleChange={values => handleOrderTypeOnChange(values)}
          optional
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
        />

        {!isCompletedTaskTabActive && (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
            <DatePicker
              label={'Last updated'}
              value={lastUpdatedDate}
              onChange={val => {
                const formattedValue = val
                  ? dayjs(val).format('MMM D, YYYY')
                  : '';
                handleLastUpdatedDateOnChange(formattedValue);
              }}
            />
          </LocalizationProvider>
        )}

        <MultiSelect
          name='location'
          size='medium'
          label='Location'
          multiple
          onChange={handleLocationOnChange}
          value={draftFilters.location_building_ids}
          options={locationOptions}
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '0.5rem',
          width: '100%',
          py: '1rem',
        }}
      >
        <Button
          variant='outlined'
          onClick={handleClearAll}
          sx={{ width: '100%', maxHeight: '2rem' }}
          disabled={!isResetEnabled}
        >
          {t('general.reset')}
        </Button>
        <Button
          onClick={handleApply}
          variant='contained'
          color='secondary'
          loadingPosition='center'
          disabled={!isApplyEnabled}
          sx={{
            color: 'white',
            width: '100%',
            maxHeight: '2rem',
          }}
        >
          {t('general.apply')}
        </Button>
      </Box>
    </Box>
  );
};
